import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, inr } from "../lib/api";
import { useSeo, Reveal } from "../components/site/Seo";
import { ProductCard, SectionHeader } from "../components/site/Cards";
import { examIcon } from "../components/site/icons";
import { Button } from "../components/ui/button";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import { track } from "../lib/tracking";
import { CheckCircle2, Layers, HelpCircle, Globe, Gauge, ArrowRight } from "lucide-react";
import NotFound from "./NotFound";

export default function ExamPage() {
  const { examSlug } = useParams();
  const [exam, setExam] = useState(undefined);
  const [faqs, setFaqs] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    setExam(undefined);
    api.get(`/exams/${examSlug}`).then((r) => {
      setExam(r.data);
      track("ViewContent", { content_type: "exam", content_name: r.data.name });
    }).catch(() => setExam(null));
    api.get(`/faqs?exam=${examSlug}`).then((r) => setFaqs(r.data)).catch(() => {});
    api.get(`/testimonials`).then((r) => setTestimonials(r.data.slice(0, 3))).catch(() => {});
  }, [examSlug]);

  useSeo({ title: exam ? `${exam.name} Test Series | Agni Shiksha` : "Agni Shiksha",
    description: exam?.hero_subtext });

  if (exam === null) return <NotFound />;
  if (!exam) return <div className="container-px py-40 text-center text-content-muted">Loading…</div>;

  const Icon = examIcon(exam.icon);
  const products = exam.products || [];
  const cheapest = products.filter((p) => p.price > 0).sort((a, b) => a.price - b.price)[0];

  return (
    <div className="pb-24">
      {/* Hero */}
      <section className="hero-glow container-px pt-14 pb-16 lg:pt-20">
        <div className="max-w-3xl">
          <div className="w-12 h-12 rounded-xl bg-ember-soft flex items-center justify-center mb-5"><Icon className="w-6 h-6 text-ember" /></div>
          <p className="text-xs uppercase tracking-widest text-ember mb-3">{exam.category} • {exam.difficulty}</p>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight">{exam.hero_headline || exam.name}</h1>
          <p className="text-content-secondary text-lg mt-5 leading-relaxed">{exam.hero_subtext || exam.description}</p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Button asChild size="lg" className="btn-ember rounded-full h-12 px-7"><a href="#series">View Test Series</a></Button>
            <Button asChild size="lg" variant="outline" className="rounded-full h-12 px-7 border-line bg-card"><Link to="/free-tests">Free Mock</Link></Button>
          </div>
        </div>
        {(exam.benefits || []).length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-12 max-w-4xl">
            {exam.benefits.map((b, i) => (
              <div key={i} className="flex items-start gap-2.5 text-sm"><CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /> <span className="text-content-secondary">{b}</span></div>
            ))}
          </div>
        )}
      </section>

      {/* Meta cards */}
      <section className="container-px grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          { icon: Layers, label: "Exam Pattern", value: exam.exam_pattern },
          { icon: Globe, label: "Languages", value: (exam.languages || []).join(", ") },
          { icon: Gauge, label: "Difficulty", value: exam.difficulty },
          { icon: HelpCircle, label: "PYQ Available", value: exam.pyq_available ? "Yes" : "Coming soon" },
        ].map((m, i) => (
          <div key={i} className="card-surface p-5">
            <m.icon className="w-5 h-5 text-ember mb-2" />
            <div className="text-xs text-content-muted uppercase tracking-wide mb-1">{m.label}</div>
            <div className="text-sm text-content-secondary line-clamp-3">{m.value}</div>
          </div>
        ))}
      </section>

      {/* Syllabus */}
      {exam.syllabus && (
        <section className="container-px mb-8">
          <div className="card-surface p-6">
            <h3 className="font-heading font-semibold mb-2">Syllabus Coverage</h3>
            <p className="text-sm text-content-secondary leading-relaxed">{exam.syllabus}</p>
          </div>
        </section>
      )}

      {/* Products */}
      <section id="series" className="container-px section-y">
        <SectionHeader eyebrow="Available Test Series" title={`${exam.name} test series`}
          subtitle="Pick the package that matches your target. Each series includes a free demo test." />
        {products.length === 0 ? (
          <div className="card-surface p-10 text-center text-content-muted">Test series for this exam are coming soon.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((p, i) => <Reveal key={p.id} delay={i * 60}><ProductCard p={p} /></Reveal>)}
          </div>
        )}
      </section>

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section className="container-px section-y">
          <SectionHeader eyebrow="Social proof" title="Aspirants trust Agni Shiksha" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {testimonials.map((t) => (
              <div key={t.id} className="card-surface p-6">
                <p className="text-sm text-content-secondary mb-4">“{t.text}”</p>
                <div className="text-sm font-medium">{t.name}</div>
                <div className="text-xs text-content-muted">{t.exam} • {t.city}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* FAQ */}
      {faqs.length > 0 && (
        <section className="container-px section-y max-w-3xl">
          <SectionHeader eyebrow="FAQ" title={`${exam.name} — your questions`} center />
          <Accordion type="single" collapsible>
            {faqs.map((f) => (
              <AccordionItem key={f.id} value={f.id} className="card-surface mb-3 px-5 border">
                <AccordionTrigger className="text-left text-sm font-medium hover:no-underline">{f.question}</AccordionTrigger>
                <AccordionContent className="text-sm text-content-secondary">{f.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      )}

      {/* Sticky mobile CTA */}
      {cheapest && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 glass-nav border-t border-line px-4 py-3 flex items-center justify-between">
          <div>
            <div className="text-xs text-content-muted">From</div>
            <div className="font-heading font-bold text-lg">{inr(cheapest.price)}</div>
          </div>
          <Button asChild className="btn-ember rounded-full px-6" data-testid="exam-sticky-cta">
            <Link to={`/product/${cheapest.slug}`}>Buy Test Series <ArrowRight className="w-4 h-4 ml-1" /></Link>
          </Button>
        </div>
      )}
    </div>
  );
}
